import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { TenantProvider } from "@/context/TenantContext";
import { I18nProvider } from "@/context/I18nContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import ComingSoon from "@/components/ComingSoon";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import CompanyProfile from "@/pages/CompanyProfile";
import UsersRoles from "@/pages/UsersRoles";
import ReportingPeriods from "@/pages/ReportingPeriods";
import OnboardingAutofill from "@/pages/OnboardingAutofill";
import PercorsoGuidato from "@/pages/PercorsoGuidato";
import AuditLog from "@/pages/AuditLog";
import Jobtimehr from "@/pages/Jobtimehr";
import EsgPillars from "@/pages/EsgPillars";
import Settings from "@/pages/Settings";
import Vsme from "@/pages/Vsme";
import VsmeDisclosure from "@/pages/VsmeDisclosure";
import AdminStandard from "@/pages/AdminStandard";
import Questionnaire from "@/pages/Questionnaire";
import QuestionnaireReview from "@/pages/QuestionnaireReview";
import MefRiepilogo from "@/pages/MefRiepilogo";
import Incongruenze from "@/pages/Incongruenze";
import AdminQuestions from "@/pages/AdminQuestions";
import Environmental from "@/pages/Environmental";
import AdminFactors from "@/pages/AdminFactors";
import Social from "@/pages/Social";
import SocialReview from "@/pages/SocialReview";
import MefDashboard from "@/pages/MefDashboard";
import AdminFrameworks from "@/pages/AdminFrameworks";
import AdminMefIndicators from "@/pages/AdminMefIndicators";
import AdminDemoRequests from "@/pages/AdminDemoRequests";
import ImprovementPlan from "@/pages/ImprovementPlan";
import Simulator from "@/pages/Simulator";
import Portfolio from "@/pages/Portfolio";
import ConsultantNotes from "@/pages/ConsultantNotes";
import AttivitaRevisione from "@/pages/AttivitaRevisione";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import ModuleRoute from "@/components/ModuleRoute";
import ConsultantRoute from "@/components/ConsultantRoute";
import Valutazione from "@/pages/Valutazione";
import AdminMetodologie from "@/pages/AdminMetodologie";
import Governance from "@/pages/Governance";
import Eva from "@/pages/Eva";
import EvaAdmin from "@/pages/EvaAdmin";
import AdminLanguages from "@/pages/AdminLanguages";
import AdminNormativa from "@/pages/AdminNormativa";
import AdminSystem from "@/pages/AdminSystem";
import Privacy from "@/pages/Privacy";
import Termini from "@/pages/Termini";
import AcceptInvite from "@/pages/AcceptInvite";
import ResetPassword from "@/pages/ResetPassword";
import PlatformDashboard from "@/pages/admin/PlatformDashboard";
import PlatformOrganizations from "@/pages/admin/PlatformOrganizations";
import PlatformConsultants from "@/pages/admin/PlatformConsultants";
import PlatformCompanies from "@/pages/admin/PlatformCompanies";
import PlatformCompanyDetail from "@/pages/admin/PlatformCompanyDetail";
import PlatformUsers from "@/pages/admin/PlatformUsers";
import PlatformUserDetail from "@/pages/admin/PlatformUserDetail";
import PlatformRoles from "@/pages/admin/PlatformRoles";
import PlatformAssignments from "@/pages/admin/PlatformAssignments";
import PlatformFeatures from "@/pages/admin/PlatformFeatures";
import PlatformInvitations from "@/pages/admin/PlatformInvitations";
import PlatformImport from "@/pages/admin/PlatformImport";
import PlatformTechDebt from "@/pages/admin/PlatformTechDebt";
import PlatformAudit from "@/pages/admin/PlatformAudit";
import PlatformIntegrations from "@/pages/admin/PlatformIntegrations";
import TemplateCenter from "@/pages/admin/TemplateCenter";
import OrgConsole from "@/pages/OrgConsole";
import Landing from "@/pages/Landing";
import RequestDemo from "@/pages/RequestDemo";
import GuidePage, { GuideIndexPage } from "@/pages/public/Guides";
import PublicOrPrivate from "@/components/public/PublicOrPrivate";
import {
  VsmePage, PmiBanchePage, EnvironmentalPage, SocialPage, GovernancePage,
  CarbonCalculatorPage, CassettoEsgPage, EvaPage, JobtimehrPage,
  ConsulentiPage, PmiPage, ChiSiamoPage, NotFoundPage,
} from "@/pages/public";
import Documents from "@/pages/Documents";
import Report from "@/pages/Report";
import ReportDetail from "@/pages/ReportDetail";
import ExternalQuestionnaires from "@/pages/ExternalQuestionnaires";
import ExternalQuestionnaireDetail from "@/pages/ExternalQuestionnaireDetail";
import ExternalTemplateDetail from "@/pages/ExternalTemplateDetail";
import DataRoom from "@/pages/DataRoom";
import DataRoomDetail from "@/pages/DataRoomDetail";
import DataRoomPortal from "@/pages/DataRoomPortal";
import {
  Target, HelpCircle, BarChart3, FolderLock, Sparkles,
} from "lucide-react";

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <I18nProvider>
        <TenantProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/richiedi-demo" element={<RequestDemo />} />
              {/* Pagine pubbliche SEO. Per le route condivise con l'area riservata,
                  PublicOrPrivate mostra la pagina pubblica agli anonimi e l'app agli autenticati. */}
              <Route path="/vsme/*" element={<PublicOrPrivate pub={<VsmePage />}
                priv={<ModuleRoute module="VSME"><Vsme /></ModuleRoute>} />} />
              <Route path="/pmi-banche" element={<PublicOrPrivate pub={<PmiBanchePage />}
                priv={<ConsultantRoute perm="analysis:dossier:view"><ModuleRoute module="MEF"><MefDashboard /></ModuleRoute></ConsultantRoute>} />} />
              <Route path="/environmental" element={<PublicOrPrivate pub={<EnvironmentalPage />}
                priv={<ModuleRoute module="ENVIRONMENTAL"><Environmental /></ModuleRoute>} />} />
              <Route path="/social/*" element={<PublicOrPrivate pub={<SocialPage />}
                priv={<ModuleRoute module="SOCIAL"><Social /></ModuleRoute>} />} />
              <Route path="/governance" element={<PublicOrPrivate pub={<GovernancePage />}
                priv={<ModuleRoute module="GOVERNANCE"><Governance /></ModuleRoute>} />} />
              <Route path="/eva" element={<PublicOrPrivate pub={<EvaPage />}
                priv={<ModuleRoute module="EVA"><Eva /></ModuleRoute>} />} />
              <Route path="/jobtimehr" element={<PublicOrPrivate pub={<JobtimehrPage />}
                priv={<ModuleRoute module="JOBTIMEHR"><Jobtimehr /></ModuleRoute>} />} />
              <Route path="/carbon-calculator" element={<CarbonCalculatorPage />} />
              <Route path="/cassetto-esg" element={<CassettoEsgPage />} />
              <Route path="/consulenti" element={<ConsulentiPage />} />
              <Route path="/pmi" element={<PmiPage />} />
              <Route path="/chi-siamo" element={<ChiSiamoPage />} />
              <Route path="/guide" element={<GuideIndexPage />} />
              <Route path="/guide/:slug" element={<GuidePage />} />
              <Route path="/data-room/:token" element={<DataRoomPortal />} />
              <Route path="/login" element={<Login />} />
              <Route path="/superadmin/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/termini" element={<Termini />} />
              <Route path="/attiva/:token" element={<AcceptInvite />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                <Route path="/superadmin/dashboard" element={<Navigate to="/admin/platform" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/esg" element={<ModuleRoute module="VSME"><EsgPillars /></ModuleRoute>} />
                <Route path="/vsme/disclosure/:code" element={<VsmeDisclosure />} />
                <Route path="/admin/standard" element={<AdminStandard />} />
                <Route path="/questionario" element={<ModuleRoute module="VSME"><Questionnaire /></ModuleRoute>} />
                <Route path="/questionario/riepilogo" element={<ModuleRoute module="VSME"><QuestionnaireReview /></ModuleRoute>} />
                <Route path="/questionario-mef" element={<ModuleRoute module="MEF"><Questionnaire fixedSection="DIALOGO_BANCARIO" /></ModuleRoute>} />
                <Route path="/questionario-mef/riepilogo" element={<ModuleRoute module="MEF"><MefRiepilogo /></ModuleRoute>} />
                <Route path="/incongruenze" element={<ModuleRoute module="VSME"><Incongruenze /></ModuleRoute>} />
                <Route path="/social/review" element={<ModuleRoute module="SOCIAL"><SocialReview /></ModuleRoute>} />
                <Route path="/portafoglio" element={<ConsultantRoute perm="analysis:score:view"><ModuleRoute module="PORTFOLIO"><Portfolio /></ModuleRoute></ConsultantRoute>} />
                <Route path="/valutazione" element={<ConsultantRoute perm="analysis:score:view"><ModuleRoute module="SCORE"><Valutazione /></ModuleRoute></ConsultantRoute>} />
                <Route path="/admin/metodologie" element={<AdminMetodologie />} />
                <Route path="/note-interne" element={<ConsultantRoute perm="analysis:notes:view"><ConsultantNotes /></ConsultantRoute>} />
                <Route path="/attivita" element={<ConsultantRoute perm="esg:verify"><AttivitaRevisione /></ConsultantRoute>} />
                <Route path="/admin/frameworks" element={<AdminFrameworks />} />
                <Route path="/admin/indicatori-mef" element={<AdminMefIndicators />} />
                <Route path="/admin/demo-requests" element={<AdminDemoRequests />} />
                <Route path="/admin/eva" element={<EvaAdmin />} />
                <Route path="/admin/lingue" element={<AdminLanguages />} />
                <Route path="/admin/normativa/*" element={<AdminNormativa />} />
                <Route path="/admin/regolatorio" element={<Navigate to="/admin/normativa" replace />} />
                <Route path="/admin/sistema" element={<AdminSystem />} />
                <Route path="/admin/platform" element={<PlatformDashboard />} />
                <Route path="/admin/platform/studi" element={<PlatformOrganizations />} />
                <Route path="/admin/platform/consulenti" element={<PlatformConsultants />} />
                <Route path="/admin/platform/aziende" element={<PlatformCompanies />} />
                <Route path="/admin/platform/aziende/:companyId" element={<PlatformCompanyDetail />} />
                <Route path="/admin/platform/utenti" element={<PlatformUsers />} />
                <Route path="/admin/platform/utenti/:userId" element={<PlatformUserDetail />} />
                <Route path="/admin/platform/ruoli" element={<PlatformRoles />} />
                <Route path="/admin/platform/assegnazioni" element={<PlatformAssignments />} />
                <Route path="/admin/platform/moduli" element={<PlatformFeatures />} />
                <Route path="/admin/platform/inviti" element={<PlatformInvitations />} />
                <Route path="/admin/platform/importa" element={<PlatformImport />} />
                <Route path="/admin/platform/audit" element={<PlatformAudit />} />
                <Route path="/admin/platform/integrazioni" element={<PlatformIntegrations />} />
                <Route path="/admin/platform/tech-debt" element={<PlatformTechDebt />} />
                <Route path="/admin/report-templates" element={<TemplateCenter />} />
                <Route path="/studio" element={<OrgConsole />} />
                <Route path="/admin/questions" element={<AdminQuestions />} />
                <Route path="/admin/factors" element={<AdminFactors />} />
                <Route path="/documenti" element={<ModuleRoute module="DOCUMENT_AI"><Documents /></ModuleRoute>} />
                <Route path="/piano-miglioramento" element={<ConsultantRoute perm="analysis:plan:view"><ModuleRoute module="IMPROVEMENT_PLAN"><ImprovementPlan /></ModuleRoute></ConsultantRoute>} />
                <Route path="/obiettivi" element={<ComingSoon icon={Target} title="Obiettivi ESG" description="La definizione e il monitoraggio degli obiettivi ESG (target, baseline, scadenze) sarà disponibile nelle prossime fasi." />} />
                <Route path="/simulazioni" element={<ConsultantRoute perm="analysis:simulation:run"><ModuleRoute module="SIMULATOR"><Simulator /></ModuleRoute></ConsultantRoute>} />
                <Route path="/questionari" element={<Navigate to="/questionari-esterni" replace />} />
                <Route path="/questionari-esterni" element={<ModuleRoute module="EXTERNAL_QUESTIONNAIRES"><ExternalQuestionnaires /></ModuleRoute>} />
                <Route path="/questionari-esterni/template/:templateId" element={<ConsultantRoute perm="externalq:manage"><ExternalTemplateDetail /></ConsultantRoute>} />
                <Route path="/questionari-esterni/:instanceId" element={<ExternalQuestionnaireDetail />} />
                <Route path="/data-room-interna" element={<ConsultantRoute perm="dataroom:view"><ModuleRoute module="DATA_ROOM"><DataRoom /></ModuleRoute></ConsultantRoute>} />
                <Route path="/data-room-interna/:roomId" element={<ConsultantRoute perm="dataroom:view"><DataRoomDetail /></ConsultantRoute>} />
                <Route path="/report" element={<ConsultantRoute perm="analysis:report:view"><ModuleRoute module="REPORTING"><Report /></ModuleRoute></ConsultantRoute>} />
                <Route path="/report/:reportId" element={<ConsultantRoute perm="analysis:report:view"><ModuleRoute module="REPORTING"><ReportDetail /></ModuleRoute></ConsultantRoute>} />
                <Route path="/audit-log" element={<AuditLog />} />
                <Route path="/azienda" element={<CompanyProfile />} />
                <Route path="/utenti" element={<UsersRoles />} />
                <Route path="/periodi" element={<ReportingPeriods />} />
                <Route path="/onboarding" element={<OnboardingAutofill />} />
                <Route path="/percorso" element={<ModuleRoute module="VSME"><PercorsoGuidato /></ModuleRoute>} />
                <Route path="/impostazioni" element={<Settings />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/cancel" element={<PaymentCancel />} />
              </Route>
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </TenantProvider>
        </I18nProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
